import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * React-controlled Bootstrap dropdown.
 *
 * The Razor pages used data-bs-toggle="dropdown" plus bootstrap.bundle.js. That library is not
 * loaded here (it fights React over the DOM), so the open state and outside-click dismissal are
 * handled in React while the markup and classes stay identical.
 *
 * POSITIONING IS OURS TOO, and that is less obvious. Bootstrap 5 scopes every directional rule to
 * the [data-bs-popper] attribute:
 *
 *     .dropdown-menu[data-bs-popper]       { top: 100%; left: 0; }
 *     .dropup .dropdown-menu[data-bs-popper] { top: auto; bottom: 100%; }
 *
 * Popper writes that attribute, and Popper is part of the bundle we do not load - so the classes
 * alone do nothing. A menu left at `top: auto; left: auto` renders at its static position, which
 * is below the toggle whatever the container class says, and resolves against the nearest
 * POSITIONED ancestor rather than the dropdown. In the sidebar footer that ancestor was the fixed
 * .sidebar, and an absolutely-positioned child overflowing its `overflow-y: auto` box extends the
 * scrollable area - the account menu opened downward and grew a scrollbar on the whole sidebar.
 *
 * So the container is explicitly relative and the menu is placed by hand from `containerClassName`
 * and `align`.
 *
 * `fixed` is for menus inside a scroll box such as `.table-responsive`: its overflow clips an
 * absolutely placed menu (it hid behind the card and scrolled the table when there were few rows).
 * The menu is then placed against the viewport from the toggle's position, opens upward when it
 * would not fit below, and closes on scroll or resize rather than drift away from its row.
 */
export function Dropdown({
  toggle,
  children,
  align = 'end',
  toggleClassName = 'btn btn-sm btn-outline-secondary',
  menuClassName = '',
  containerClassName = 'dropdown',
  fixed = false,
}: {
  toggle: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  /** Overrides the toggle's classes. Defaults to the table-row button this started life as. */
  toggleClassName?: string;
  /** Extra classes for the menu, e.g. a shadow on a dark background. */
  menuClassName?: string;
  /**
   * Replaces the container class. Pass "dropup" where the toggle sits at the bottom of a
   * clipping container — the sidebar footer opens upward for exactly that reason.
   */
  containerClassName?: string;
  /** Place the menu against the viewport, so a clipping ancestor (a table's scroll box) cannot hide it. */
  fixed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [fixedStyle, setFixedStyle] = useState<CSSProperties | null>(null);

  // Measured before paint, so the menu never flashes at the wrong place.
  useLayoutEffect(() => {
    if (!open || !fixed || !toggleRef.current || !menuRef.current) {
      setFixedStyle(null);
      return;
    }
    const toggleBox = toggleRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const gap = 2;
    const fitsBelow = toggleBox.bottom + gap + menuHeight <= window.innerHeight;
    setFixedStyle({
      position: 'fixed',
      top: fitsBelow ? toggleBox.bottom + gap : Math.max(gap, toggleBox.top - gap - menuHeight),
      bottom: 'auto',
      margin: 0,
      left:  align === 'start' ? toggleBox.left : 'auto',
      right: align === 'end' ? document.documentElement.clientWidth - toggleBox.right : 'auto',
    });
  }, [open, fixed, align]);

  useEffect(() => {
    if (!open || !fixed) return;
    const close = () => setOpen(false);
    // Capture: a scroll inside any container (the table's own scroll box included) moves the row.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, fixed]);

  useEffect(() => {
    if (!open) return;

    function onDocumentClick(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // "dropup" is the Bootstrap class name, kept so the markup still reads like the Razor original.
  const opensUpward = containerClassName.split(/\s+/).includes('dropup');

  const menuStyle: CSSProperties = fixed
    ? // Until measured, laid out fixed but invisible so its height can be read.
      fixedStyle ?? { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }
    : {
        position: 'absolute',
        top:    opensUpward ? 'auto' : '100%',
        bottom: opensUpward ? '100%' : 'auto',
        marginTop:    opensUpward ? 0 : '0.125rem',
        marginBottom: opensUpward ? '0.125rem' : 0,
        // Horizontal edge to pin to; the matching dropdown-menu-{start,end} class is also applied, but
        // it too is inert without Popper.
        left:  align === 'start' ? 0 : 'auto',
        right: align === 'end' ? 0 : 'auto',
      };

  return (
    // position: relative makes THIS the containing block. Without it the menu resolves against
    // whatever positioned ancestor happens to be above it.
    <div className={containerClassName} ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={toggleRef}
        type="button"
        className={toggleClassName}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {toggle}
      </button>
      {open && (
        <ul
          ref={menuRef}
          className={`dropdown-menu dropdown-menu-${align} show ${menuClassName}`}
          style={menuStyle}
        >
          {children(() => setOpen(false))}
        </ul>
      )}
    </div>
  );
}
